import get from 'lodash/get';
import invoke from 'lodash/invoke';
import { BuildingBlock, BuildingBlockLayouts } from '@pendo/services/BuildingBlocks';
import { isCrossApp } from '@pendo/services/CrossAppGuides';
import { http } from '@pendo/http';
import { WEB_GUIDE_PUBLISHED } from '../track-events-types.js';
import { ACTIVATION_TYPES } from '@pendo/services/Constants';

export async function fireWebGuidePublishTrackEvents (guide, platform, numOfVisitorsInSegment, guideCategoryName) {
    let visitorId;
    let accountId;
    let stepsData;

    try {
        visitorId = invoke(window, 'pendo.get_visitor_id');
        accountId = invoke(window, 'pendo.get_account_id');
        stepsData = await getStepsData(guide.steps);
    } catch {
        return;
    }

    const guideMetaDataProperties = {
        guideId: guide.id,
        appId: guide.appId,
        visitorId,
        accountId,
        category: guideCategoryName || 'No Guide category',
        segmentVisitors: numOfVisitorsInSegment,
    };

    const guideData = {
        activationAuto: guide.launchMethod.includes(ACTIVATION_TYPES.AUTOMATICALLY_OR_APP_LAUNCH),
        activationBadge: guide.launchMethod.includes(ACTIVATION_TYPES.BADGE),
        activationEmbedded: guide.launchMethod === ACTIVATION_TYPES.EMBEDDED,
        activationTargetElement: guide.launchMethod.includes(ACTIVATION_TYPES.TARGET_ELEMENT),
        activationApi: guide.launchMethod === ACTIVATION_TYPES.API,
        resourceCenter: guide.launchMethod.includes(ACTIVATION_TYPES.PENDO_RESOURCE_CENTER),
        repeatGuideDisplay: !!guide.redisplay,
        repeatCapping: guide.redisplay ? guide.redisplay.maxDismissals : 0,
        steps: guide.steps.length,
        nps: !!guide.polls?.find((poll) => poll.attributes.type === 'NPSRating'),
        crossApp: !!isCrossApp(guide),
        announcement: !!guide.attributes.isAnnouncement,
        platform,
        guideVersion: guide.attributes.type === 'building-block' ? 'v2' : 'v1',
        wasCreatedByConversationalAi: !!guide.attributes.createdByConversationalAi,
        deviceType: guide.attributes.device.type,
        conversion: !!guide.conversion,
    };

    window.pendo.track(WEB_GUIDE_PUBLISHED, {
        ...guideMetaDataProperties,
        ...guideData,
        ...stepsData,
    });
}

async function getStepsData (steps) {
    let centeredSteps = 0;
    let relativeToElementSteps = 0;
    let caretSteps = 0;
    let pageLocationSteps = 0;
    let sitewideLocationSteps = 0;
    let codeBlocksSteps = 0;
    let videoBlocksSteps = 0;
    let stepsBuildingBlocksList;

    try {
        // get all building blocks from all steps
        stepsBuildingBlocksList = await getBuildingBlocksListFromSteps(steps);
    } catch {
        throw new Error();
    }

    // gather data from stepsBuildingBlocksList
    stepsBuildingBlocksList.forEach((buildingBlocksObj) => {
        const mainBlock = BuildingBlock.findBlockByDomId(buildingBlocksObj, 'pendo-g-');
        const containerBlock = BuildingBlock.findBlockByDomId(buildingBlocksObj, 'pendo-guide-container');
        const codeBlock = BuildingBlock.findBlockByWidgetId(
            buildingBlocksObj,
            BuildingBlockLayouts.widgetIds.codeBlock,
        );
        const videoBlock = BuildingBlock.findBlockByWidgetId(
            buildingBlocksObj,
            BuildingBlockLayouts.widgetIds.videoBlock,
        );
        const verticalAlignment = get(mainBlock, 'web.verticalAlignment', '');
        const caret = get(containerBlock, 'web.caret');

        // calc vertical alignment
        if (verticalAlignment === 'Centered') {
            centeredSteps += 1;
        } else if (verticalAlignment === 'Relative to Element') {
            relativeToElementSteps += 1;
        }
        // calc caret
        if (caret?.enabled) {
            caretSteps += 1;
        } else {
            sitewideLocationSteps += 1;
        }
        // calc code block
        if (codeBlock) {
            codeBlocksSteps += 1;
        }
        // calc video block
        if (videoBlock) {
            videoBlocksSteps += 1;
        }
    });

    // calc location
    steps.forEach((step) => {
        if (step.pageId) {
            pageLocationSteps += 1;
        }
    });

    return {
        centeredSteps,
        relativeToElementSteps,
        uncenteredAndUnrelativeSteps: steps.length - centeredSteps - relativeToElementSteps,
        caretSteps,
        pageLocationSteps,
        sitewideLocationSteps,
        codeBlocksSteps,
        videoBlocksSteps,
    };
}

async function getBuildingBlocksListFromSteps (steps) {
    const stepsBuildingBlocksList = [];
    const buildingBlocksPromisesList = [];

    steps.forEach((step) => {
        let { buildingBlocks } = step;
        if (typeof buildingBlocks === 'undefined') {
            const buildingBlocksPromise = http.get(step.buildingBlocksUrl, { withCredentials: false });
            buildingBlocksPromisesList.push(buildingBlocksPromise);
        } else {
            if (typeof buildingBlocks === 'string') {
                buildingBlocks = JSON.parse(buildingBlocks);
            }

            stepsBuildingBlocksList.push(buildingBlocks);
        }
    });

    if (buildingBlocksPromisesList.length > 0) {
        const buildingBlocksPromisesRes = await Promise.all(buildingBlocksPromisesList);
        buildingBlocksPromisesRes?.forEach((res) => {
            stepsBuildingBlocksList.push(res.data);
        });
    }

    return stepsBuildingBlocksList;
}
